import type { TaskStatus } from "../../lib/types";
import type { IntakeSource } from "../intakeDetails/types";

export interface ProjectItem {
  id: string;
  name: string;
  status: string;
  type?: string;
  source?: IntakeSource;
  startDate?: string;
  endDate?: string;
  vendorName?: string;
  vendorPartners?: string[];
  intakeId?: string;
  moaFileUrl?: string;
  createdAt?: string;
  aiSummary?: string;
  aiSummaryUpdatedAt?: string;
  /** OAIP-91 — one-sentence headline used by the Leadership PowerPoint slides 5/6 tables. */
  aiHeadline?: string;
  aiHeadlineUpdatedAt?: string;
  projectDescription?: string;
  problemBeingSolved?: string;
  currentProblemImpact?: string;
  successIndicators?: string;
  successMetrics?: string;
  impactReach?: string;
  dataAvailability?: string;
  usesLowRiskData?: boolean;
  dataSources?: string;
  historicalDataAmount?: string;
  preferredVendorTech?: string;
  ethicsDecisionMaking?: string;
  risks?: string;
  technicalPOC?: string;
  businessPOC?: string;
  executiveSupport?: string;
  otherNotes?: string;
  /** OAIP-48 — executive dashboard impact spotlight fields. */
  impactHeadline?: string;
  impactStory?: string;
  impactMetrics?: Array<{ value: string; label: string }>;
  impactImageUrl?: string;
  impactLastReviewedAt?: string;
  impactReviewedBy?: string;
  spotlightFeatured?: boolean;
}

export interface Vendor {
  id: string;
  profile?: string;
  capabilityTags?: string[];
  status?: string;
}

export interface CommentItem {
  id: string;
  projectId: string;
  message: string;
  createdAt?: string;
  author?: string;
}

export interface AttachmentItem {
  id: string;
  projectId: string;
  name: string;
  url: string;
  type?: string;
  createdAt?: string;
}

export interface UserOption {
  uid: string;
  displayName: string;
  email: string;
}

export type TabType =
  | "summary"
  | "details"
  | "plan"
  | "metrics"
  | "comments"
  | "files";

export const statusOptions = [
  {
    group: "Workflow",
    options: [
      "Submitted",
      "Had Scoping Call",
      "Initial Intake Conversation Complete",
      "Evaluation Template Complete",
      "AI Advisory Board Approved",
      "AI Steer Co Approved",
      "In Progress",
    ],
  },
  {
    group: "On Hold",
    options: ["On Hold (High-Risk Data)", "On Hold (Other)"],
  },
  {
    group: "Closed",
    options: [
      "Completed",
      "Completed, Story Told",
      "Ended Early",
      "Rejected (Not Accelerator Project)",
      "Rejected (Other)",
    ],
  },
];

export const CLOSED_STATUSES = new Set<string>(statusOptions[2].options);

export const projectStageOptions = statusOptions.flatMap((group) => group.options);

export const taskStatusOptions: Array<{ value: TaskStatus; label: string }> = [
  { value: "todo", label: "To do" },
  { value: "in_progress", label: "In progress" },
  { value: "blocked", label: "Blocked" },
  { value: "done", label: "Done" },
];

export function getStatusColor(status: string): string {
  switch (status) {
    case "Submitted":
      return "default";
    case "Had Scoping Call":
      return "blue";
    case "Initial Intake Conversation Complete":
      return "geekblue";
    case "Evaluation Template Complete":
      return "purple";
    case "AI Advisory Board Approved":
      return "cyan";
    case "AI Steer Co Approved":
      return "magenta";
    case "In Progress":
      return "processing";
    case "Completed":
    case "Completed, Story Told":
      return "success";
    case "On Hold (High-Risk Data)":
    case "On Hold (Other)":
      return "gold";
    case "Ended Early":
      return "default";
    case "Rejected (Not Accelerator Project)":
    case "Rejected (Other)":
      return "error";
    default:
      return "default";
  }
}
